/**
 * PROJECT AND CHARACTER NAMES, REMEMBERED FOR THE NEXT SCREEN (0.1.398).
 *
 * ⚠️ WHY THIS EXISTS AT ALL. A picker's back chip names where it returns to — *"← Panenka"*, not
 * *"← Project"*. The name lives on the server, so the picker fetched it, and until that landed the
 * chip read a placeholder and then changed under the eye: *"it very briefly shows `this project`
 * in the back button, and then it switches to the project name"* (maintainer, 26 Aug). A label
 * that rewrites itself is worse than one that waits, and the reason it had to ask is silly — the
 * screen you came FROM had just read that name.
 *
 * So: whichever screen reads a project or a character writes the names it saw here, and the picker
 * reads them SYNCHRONOUSLY on its first render. In the ordinary path — project detail → its
 * picker — the name is already in hand and the chip is correct on the first paint.
 *
 * ⚠️ IT IS A CONVENIENCE, NEVER AN AUTHORITY. Every reader still fetches, and the fetched answer
 * replaces whatever was remembered; a rename in another session must not be papered over by a
 * stale label. A miss simply means the chip waits for the read, which is the old behaviour. It is
 * deliberately in-memory only: persisted, it would outlive the rename that invalidated it.
 */

const projects = new Map<string, string>();
const characters = new Map<string, string>();

/** Remember what a read just proved. Called by the screens that list or open these entities. */
export function rememberProjectNames(rows: { id: string; name: string }[]): void {
  for (const r of rows) projects.set(r.id, r.name);
}

export function rememberCharacterNames(rows: { id: string; name: string }[]): void {
  for (const r of rows) characters.set(r.id, r.name);
}

/** The remembered name, or null — never a guess, and never a placeholder dressed as an answer. */
export function rememberedProjectName(id: string | undefined): string | null {
  return id === undefined ? null : (projects.get(id) ?? null);
}

export function rememberedCharacterName(id: string | undefined): string | null {
  return id === undefined ? null : (characters.get(id) ?? null);
}
